import { Injectable } from '@angular/core';
import { MediaFile } from '../models/media.model';

/**
 * Derives thumbnails for media that ships without a pre-generated `thumbnailUrl`.
 *
 * - Images resolve to their own `url` (the browser scales them in a CSS box).
 * - Videos are decoded off-screen and a single frame is captured to a canvas as a data-URI.
 *
 * Stateless and `root`-provided: it operates only on the data handed to it, caching generated
 * video frames by URL so a re-render of the gallery does not re-decode the same clip. All capture
 * work is best-effort — callers fall back to a type icon when a promise rejects (e.g. CORS-tainted
 * canvas or an undecodable codec).
 */
@Injectable({ providedIn: 'root' })
export class ThumbnailService {
  // Captured frames are base64 JPEGs (tens-to-hundreds of KB each). Bound the cache so a long
  // session browsing many videos can't grow it without limit; oldest entries are evicted first.
  private static readonly MAX_CACHED_FRAMES = 50;
  private readonly videoFrameCache = new Map<string, string>();

  private cacheFrame(url: string, dataUri: string): void {
    if (this.videoFrameCache.size >= ThumbnailService.MAX_CACHED_FRAMES) {
      const oldest = this.videoFrameCache.keys().next().value;
      if (oldest !== undefined) {
        this.videoFrameCache.delete(oldest);
      }
    }
    this.videoFrameCache.set(url, dataUri);
  }

  /** Best available thumbnail source for the card, or `null` to fall back to an icon. */
  resolve(file: MediaFile): string | null {
    if (file.thumbnailUrl) {
      return file.thumbnailUrl;
    }
    if (file.type === 'image') {
      return file.url;
    }
    return this.videoFrameCache.get(file.url) ?? null;
  }

  /** Whether a cached video frame already exists (so the template can skip the icon fallback). */
  hasVideoFrame(url: string): boolean {
    return this.videoFrameCache.has(url);
  }

  /**
   * Decode a video off-screen, seek near the start and capture the first frame to a JPEG data-URI.
   * Resolves to the cached value on subsequent calls. Rejects on load/seek/draw failure.
   */
  async captureVideoFrame(url: string, seconds = 0.1, maxEdge = 480): Promise<string> {
    const cached = this.videoFrameCache.get(url);
    if (cached) {
      return cached;
    }

    const dataUri = await new Promise<string>((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.crossOrigin = 'anonymous';
      video.playsInline = true;

      const cleanup = (): void => {
        video.removeAttribute('src');
        video.load();
      };

      const onError = (): void => {
        cleanup();
        reject(new Error(`Unable to decode video: ${url}`));
      };

      video.addEventListener('error', onError, { once: true });
      video.addEventListener(
        'loadedmetadata',
        () => {
          const target = Number.isFinite(video.duration)
            ? Math.min(seconds, Math.max(0, video.duration - 0.05))
            : seconds;
          video.currentTime = target;
        },
        { once: true }
      );
      video.addEventListener(
        'seeked',
        () => {
          try {
            const width = video.videoWidth || maxEdge;
            const height = video.videoHeight || maxEdge;
            const scale = Math.min(1, maxEdge / Math.max(width, height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));
            const context = canvas.getContext('2d');
            if (!context) {
              throw new Error('2D context unavailable');
            }
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const result = canvas.toDataURL('image/jpeg', 0.7);
            cleanup();
            resolve(result);
          } catch (cause) {
            cleanup();
            reject(cause instanceof Error ? cause : new Error('Frame capture failed'));
          }
        },
        { once: true }
      );

      video.src = url;
    });

    this.cacheFrame(url, dataUri);
    return dataUri;
  }

  /**
   * Downscale an image URL to a bounded JPEG data-URI. Useful when the source images are large and
   * you want lighter grid thumbnails; images already render fine via their `url`, so this is opt-in.
   */
  async generateImageThumbnail(url: string, maxEdge = 480): Promise<string> {
    const image = await this.loadImage(url);
    const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('2D context unavailable');
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Unable to load image: ${url}`));
      image.src = url;
    });
  }
}
